'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';

// --- Types ---
interface CaseStudy {
    id: string; // 'pincode' | 'ak47'
    clientName: string;
    industry: string;
    logo: string;
    logoAlt: string;
    color: string; // Brand color for charts/accents
    stats: {
        visibility: { start: number; end: number; label: string; suffix: string };
        traffic: { start: number; end: number; label: string; suffix: '' };
        ranking: { start: number; end: number; label: string; suffix: '' };
    };
    trendData: number[]; // Array of values for the chart (6 months)
    testimonial: string;
    author: string;
    role: string;
    achievements: string[];
}

// --- Data ---
const caseStudies: Record<string, CaseStudy> = {
    pincode: {
        id: 'pincode',
        clientName: 'Pincode Hotels',
        industry: 'Hospitality',
        logo: "https://images.unsplash.com/photo-1581519925725-5ae2e08a0757",
        logoAlt: 'Luxury hotel exterior',
        color: 'text-amber-500', // Amber/Gold for Luxury/Positive (replacing Rose/Red)
        stats: {
            visibility: { start: 42, end: 89, label: 'Brand Visibility', suffix: '%' },
            traffic: { start: 1200, end: 4800, label: 'Monthly Traffic', suffix: '' },
            ranking: { start: 15, end: 3, label: 'Avg Google Rank', suffix: '' },
        },
        trendData: [1200, 1850, 2400, 3100, 3950, 4800],
        testimonial: "Site Map Genius transformed our online presence. Bookings increased by 285% within 6 months.",
        author: 'Rajesh Kumar',
        role: 'General Manager',
        achievements: [
            'Google Business Profile Verified',
            'Top 3 Local Rankings (15+ Keywords)',
            'Auto-Review Management System',
            'SEO-Optimized Content Strategy'
        ]
    },
    ak47: {
        id: 'ak47',
        clientName: 'AK47Sports',
        industry: 'E-commerce',
        logo: "https://images.unsplash.com/photo-1648657850347-81445c3978ae",
        logoAlt: 'Sports equipment',
        color: 'text-brand-primary', // Blue for E-commerce
        stats: {
            visibility: { start: 38, end: 91, label: 'Brand Visibility', suffix: '%' },
            traffic: { start: 850, end: 3400, label: 'Monthly Traffic', suffix: '' },
            ranking: { start: 22, end: 2, label: 'Avg Google Rank', suffix: '' },
        },
        trendData: [850, 1100, 1650, 2200, 2900, 3400],
        testimonial: "The Google Business Profile optimization alon brought us 150+ new customers monthly.",
        author: 'Amit Sharma',
        role: 'Founder',
        achievements: [
            '139% Visibility Increase',
            'Dominated "Cricket Gear" Niche',
            '400% Organic Traffic Growth',
            '24/7 Rank Monitoring Active'
        ]
    }
};

// --- Components ---

const MetricCard = ({ label, start, end, suffix, color }: { label: string, start: number, end: number, suffix: string, color: string }) => {
    const spring = useSpring(start, { bounce: 0, duration: 2000 });
    const display = useTransform(spring, (current) => Math.round(current).toLocaleString() + suffix);

    useEffect(() => {
        spring.set(end);
    }, [end, spring]);

    return (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex flex-col items-center justify-center">
            <motion.div className={`text-3xl font-bold ${color}`}>
                {display}
            </motion.div>
            <div className="text-xs text-slate-500 font-medium mt-1 uppercase tracking-wide">{label}</div>
        </div>
    );
};

const GrowthChart = ({ data, colorClass }: { data: number[], colorClass: string }) => {
    // Normalize data to 0-100 range for SVG
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;

    const points = data.map((val, i) => {
        const x = (i / (data.length - 1)) * 100;
        const y = 100 - ((val - min) / range) * 80; // Keep some padding
        return `${x},${y}`;
    }).join(' ');

    const areaPath = `M 0,100 ${points} 100,100 Z`;
    const linePath = `M ${points}`;

    // Resolve color code from tailwind class roughly for SVG stroke
    // A mapping helper would be better, but simple hardcodeds for now
    const strokeColor = colorClass.includes('amber') ? '#f59e0b' : '#2563eb';

    return (
        <div className="w-full h-32 relative overflow-hidden">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                <defs>
                    <linearGradient id={`grad-${strokeColor}`} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={strokeColor} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
                    </linearGradient>
                </defs>
                <motion.path
                    d={areaPath}
                    fill={`url(#grad-${strokeColor})`}
                    initial={{ opacity: 0, pathLength: 0 }}
                    animate={{ opacity: 1, pathLength: 1 }}
                    transition={{ duration: 1.5 }}
                />
                <motion.path
                    d={linePath}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth="3"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                />
            </svg>
        </div>
    );
};



const ConfettiCelebration = () => {
    // Confetti particles
    const [particles, setParticles] = useState<{
        id: number;
        x: number;
        y: number;
        color: string;
        width: number;
        height: number;
        rotation: number;
        delay: number;
        duration: number;
    }[]>([]);

    useEffect(() => {
        // Generate particles only on client
        // Premium Palette: Gold, Silver, Royal Blue, Deep Indigo
        const colors = ['#F59E0B', '#FCD34D', '#94A3B8', '#CBD5E1', '#2563EB', '#4F46E5'];

        const newParticles = Array.from({ length: 75 }).map((_, i) => {
            // Mix of shapes: mostly ribbons (rectangles) for "popper" look
            const isRibbon = Math.random() > 0.3;
            return {
                id: i,
                x: Math.random() * 100, // %
                y: -10 - Math.random() * 40, // Start slightly above view
                color: colors[Math.floor(Math.random() * colors.length)],
                width: isRibbon ? Math.random() * 8 + 6 : Math.random() * 6 + 4,
                height: isRibbon ? Math.random() * 16 + 10 : Math.random() * 6 + 4,
                rotation: Math.random() * 360,
                delay: Math.random() * 1.5,
                duration: Math.random() * 2 + 2.5
            };
        });
        setParticles(newParticles);
    }, []);

    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
            {/* Use a container motion div for the viewport trigger */}
            <motion.div
                className="w-full h-full relative"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }} // Trigger when 20% of section is visible
            >
                {particles.map((p) => (
                    <motion.div
                        key={p.id}
                        className="absolute rounded-[1px]"
                        style={{
                            backgroundColor: p.color,
                            left: `${p.x}%`,
                            width: p.width,
                            height: p.height
                        }}
                        variants={{
                            hidden: {
                                top: "-10%",
                                opacity: 0,
                                rotate: p.rotation,
                                scale: 0.8
                            },
                            visible: {
                                top: "120%",
                                opacity: [0, 1, 1, 0],
                                rotateX: [0, 360, 720],
                                rotateY: [0, 360, 720],
                                rotate: p.rotation + 360,
                                x: [0, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 200], // Sway
                                transition: {
                                    duration: p.duration,
                                    ease: "linear",
                                    delay: p.delay
                                }
                            }
                        }}
                    />
                ))}
            </motion.div>
        </div>
    );
};

const BackgroundAnimation = () => {
    // State for random elements to prevent hydration mismatch
    const [elements, setElements] = useState<{
        particles: Array<{ id: number; x: number; y: number; size: number; duration: number; delay: number }>;
        horizontalBeams: Array<{ id: string; top: number; delay: number; duration: number }>;
        verticalBeams: Array<{ id: string; left: number; delay: number; duration: number }>;
    }>({
        particles: [],
        horizontalBeams: [],
        verticalBeams: []
    });

    useEffect(() => {
        // Generate random elements only on client
        setElements({
            particles: Array.from({ length: 15 }).map((_, i) => ({
                id: i,
                x: Math.random() * 100,
                y: Math.random() * 100,
                size: Math.random() * 4 + 2,
                duration: Math.random() * 20 + 10,
                delay: Math.random() * 5
            })),
            horizontalBeams: Array.from({ length: 5 }).map((_, i) => ({
                id: `h-${i}`,
                top: Math.random() * 100,
                delay: Math.random() * 5,
                duration: Math.random() * 3 + 4
            })),
            verticalBeams: Array.from({ length: 5 }).map((_, i) => ({
                id: `v-${i}`,
                left: Math.random() * 100,
                delay: Math.random() * 5,
                duration: Math.random() * 3 + 4
            }))
        });
    }, []);

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            {/* Soft Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-transparent opacity-50" />

            {/* Animated Grid Lines */}
            <div className="absolute inset-0 opacity-[0.03]"
                style={{
                    backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)',
                    backgroundSize: '40px 40px'
                }}
            />

            {/* --- Data Beams (Moving Light Elements) --- */}

            {/* Horizontal Beams */}
            {elements.horizontalBeams.map((beam) => (
                <motion.div
                    key={beam.id}
                    className="absolute h-[1px] w-32 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-60"
                    style={{ top: `${beam.top}%`, left: '-10%' }}
                    animate={{ left: '110%' }}
                    transition={{
                        duration: beam.duration,
                        repeat: Infinity,
                        ease: "linear",
                        delay: beam.delay
                    }}
                />
            ))}

            {/* Vertical Beams */}
            {elements.verticalBeams.map((beam) => (
                <motion.div
                    key={beam.id}
                    className="absolute w-[1px] h-32 bg-gradient-to-b from-transparent via-blue-400 to-transparent opacity-60"
                    style={{ left: `${beam.left}%`, top: '-10%' }}
                    animate={{ top: '110%' }}
                    transition={{
                        duration: beam.duration,
                        repeat: Infinity,
                        ease: "linear",
                        delay: beam.delay
                    }}
                />
            ))}

            {/* --- End Data Beams --- */}

            {/* Floating Particles (Growth Indicators) */}
            {elements.particles.map((p) => (
                <motion.div
                    key={p.id}
                    className="absolute bg-brand-primary/20 rounded-full"
                    style={{
                        left: `${p.x}%`,
                        width: p.size,
                        height: p.size,
                    }}
                    initial={{ top: "110%", opacity: 0 }}
                    animate={{
                        top: "-10%",
                        opacity: [0, 0.5, 0],
                        x: `+=${Math.random() * 20 - 10}px`
                    }}
                    transition={{
                        duration: p.duration,
                        repeat: Infinity,
                        ease: "linear",
                        delay: p.delay
                    }}
                />
            ))}

            {/* Floating Charts/Icons (Abstract) */}
            <motion.div
                className="absolute top-20 right-10 text-brand-primary/5 hidden lg:block"
                animate={{ y: [0, -20, 0], opacity: [0.05, 0.1, 0.05] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
                <Icon name="PresentationChartLineIcon" size={180} />
            </motion.div>
            <motion.div
                className="absolute bottom-20 left-10 text-brand-secondary/5 hidden lg:block"
                animate={{ y: [0, 20, 0], opacity: [0.05, 0.1, 0.05] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            >
                <Icon name="ArrowTrendingUpIcon" size={140} />
            </motion.div>
        </div>
    );
};

const SuccessMetrics = () => {
    const [activeId, setActiveId] = useState<string>('pincode');
    const activeData = caseStudies[activeId];
    const [lastUpdated, setLastUpdated] = useState<string>('Today');

    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            const timeString = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            setLastUpdated(`Today, ${timeString}`);
        };

        updateTime();
    }, []);

    return (
        <section id="measurable-results" className="py-24 bg-slate-50 relative overflow-hidden scroll-mt-20">
            <BackgroundAnimation />
            <ConfettiCelebration />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center space-x-2 bg-brand-primary/10 px-4 py-2 rounded-full mb-4">
                        <Icon name="ChartBarIcon" size={20} className="text-brand-primary" variant="solid" />
                        <span className="text-sm font-medium text-brand-primary">Growth Engine</span>
                    </div>

                    <h2 className="text-4xl font-bold text-slate-900 mb-4">
                        Measurable <span className="text-brand-primary">Results</span>
                    </h2>
                    <p className="text-lg text-slate-600">
                        Real-world case studies from our partner network. Browse verifiable growth data below.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 h-full">
                    {/* Left Panel: Client Selector */}
                    {/* Left Panel: Client Selector & CTA */}
                    {/* Left Panel: Client Selector & CTA */}
                    <div className="lg:col-span-4 flex flex-col h-full">
                        {/* 1. Client Selectors (Optimized for 2 items) */}
                        <div className="space-y-4 mb-auto">
                            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 px-1">
                                Featured Partners
                            </h3>
                            {Object.values(caseStudies).map((study) => (
                                <button
                                    key={study.id}
                                    onClick={() => setActiveId(study.id)}
                                    className={`w-full text-left p-5 rounded-2xl transition-all duration-300 border flex items-center space-x-4 group relative overflow-hidden ${activeId === study.id
                                        ? 'bg-white border-brand-primary shadow-xl shadow-brand-primary/10 ring-1 ring-brand-primary/20 scale-[1.02]'
                                        : 'bg-white/60 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md'
                                        }`}
                                >
                                    {/* Active Indicator Bar */}
                                    {activeId === study.id && (
                                        <motion.div
                                            layoutId="activeBar"
                                            className="absolute left-0 top-0 bottom-0 w-1.5 bg-brand-primary"
                                        />
                                    )}

                                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-200 flex-shrink-0 relative shadow-sm">
                                        <AppImage src={study.logo} alt={study.logoAlt} className="object-cover w-full h-full" />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className={`font-bold text-lg ${activeId === study.id ? 'text-slate-900' : 'text-slate-600'}`}>
                                            {study.clientName}
                                        </h4>
                                        <p className="text-xs text-slate-500 font-medium">{study.industry}</p>
                                    </div>
                                    <div className={`transition-opacity duration-300 ${activeId === study.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-brand-primary border border-brand-primary/20 px-2 py-0.5 rounded mr-2">Case Study</span>
                                        <Icon name="ChevronRightIcon" size={20} className="text-brand-primary inline" />
                                    </div>
                                </button>
                            ))}

                            {/* "More Projects" Indicator */}
                            <div className="p-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 flex items-center space-x-4 opacity-75 hover:opacity-100 transition-opacity">
                                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                                    <Icon name="ArrowPathIcon" size={20} className="text-slate-400 animate-spin-slow" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-500 text-sm">7+ Projects Scaling...</h4>
                                    <p className="text-xs text-slate-400">Results loading soon</p>
                                </div>
                            </div>
                        </div>

                        {/* 2. Redesigned CTA Card (Royal & Thematic) */}
                        <div className="mt-8 relative group cursor-pointer overflow-hidden rounded-2xl shadow-2xl shadow-brand-primary/10">
                            {/* Animated Background Gradients */}
                            <div className="absolute inset-0 bg-slate-900">
                                <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/40 opacity-70" />
                                <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-primary/30 rounded-full blur-3xl group-hover:bg-brand-primary/40 transition-colors duration-500" />
                                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-brand-secondary/30 rounded-full blur-3xl group-hover:bg-brand-secondary/40 transition-colors duration-500" />
                            </div>

                            <div className="relative p-8 flex flex-col items-center text-center z-10">
                                <motion.div
                                    initial={{ y: 0 }}
                                    whileHover={{ y: -5 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    className="mb-4 bg-white/10 p-4 rounded-full backdrop-blur-sm border border-white/10 ring-1 ring-white/20"
                                >
                                    <Icon name="RocketLaunchIcon" size={32} className="text-brand-primary" variant="solid" />
                                </motion.div>

                                <h4 className="font-bold text-2xl text-white mb-2 tracking-tight">
                                    Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary">Scale?</span>
                                </h4>
                                <p className="text-slate-300 text-sm mb-6 leading-relaxed max-w-[200px]">
                                    Join these success stories and dominate your market.
                                </p>

                                <Link href="/contact#contact-methods" className="w-full py-3.5 bg-gradient-to-r from-brand-primary to-brand-secondary hover:from-brand-primary/90 hover:to-brand-secondary/90 text-white rounded-xl text-sm font-bold shadow-lg shadow-brand-primary/25 transition-all duration-300 transform group-hover:translate-y-[-2px] hover:shadow-brand-primary/40 text-center block">
                                    Start Your Growth
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Data Dashboard */}
                    <div className="lg:col-span-8">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden h-full flex flex-col">
                            {/* Dashboard Heater */}
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                                        <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                                        VERIFIED DATA
                                    </div>
                                    <span className="text-slate-400 text-xs text-sm">Last updated: {lastUpdated}</span>
                                </div>
                                <div className="text-slate-900 font-bold flex items-center">
                                    {activeData.clientName}
                                    <Icon name="CheckBadgeIcon" size={20} className="text-blue-500 ml-1" variant="solid" />
                                </div>
                            </div>

                            <AnimatePresence mode='wait'>
                                <motion.div
                                    key={activeId}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="p-8 flex-grow flex flex-col"
                                >
                                    {/* 1. Metrics Grid */}
                                    <div className="grid grid-cols-3 gap-4 mb-8">
                                        <MetricCard
                                            label={activeData.stats.visibility.label}
                                            start={activeData.stats.visibility.start}
                                            end={activeData.stats.visibility.end}
                                            suffix={activeData.stats.visibility.suffix}
                                            color={activeData.color}
                                        />
                                        <MetricCard
                                            label={activeData.stats.traffic.label}
                                            start={activeData.stats.traffic.start}
                                            end={activeData.stats.traffic.end}
                                            suffix={activeData.stats.traffic.suffix}
                                            color={activeData.color}
                                        />
                                        <MetricCard
                                            label={activeData.stats.ranking.label}
                                            start={activeData.stats.ranking.start}
                                            end={activeData.stats.ranking.end}
                                            suffix={activeData.stats.ranking.suffix}
                                            color={activeData.color}
                                        />
                                    </div>

                                    {/* 2. Main Chart Area */}
                                    <div className="mb-8 relative">
                                        <div className="flex justify-between items-end mb-4">
                                            <div>
                                                <h4 className="text-lg font-bold text-slate-900">Organic Growth Trajectory</h4>
                                                <p className="text-sm text-slate-500">6-Month Trend Analysis</p>
                                            </div>
                                            <div className={`text-2xl font-bold ${activeData.color}`}>
                                                +{Math.round((activeData.stats.traffic.end - activeData.stats.traffic.start) / activeData.stats.traffic.start * 100)}% Growth
                                            </div>
                                        </div>
                                        <GrowthChart data={activeData.trendData} colorClass={activeData.color} />
                                    </div>

                                    {/* 3. Achievements & Testimonial */}
                                    <div className="grid md:grid-cols-2 gap-8 mt-auto">
                                        <div className="space-y-3">
                                            <h5 className="font-semibold text-slate-900 text-sm mb-2">Key Wins</h5>
                                            {activeData.achievements.map((item, i) => (
                                                <div key={i} className="flex items-start space-x-2 text-sm text-slate-600">
                                                    <Icon name="CheckCircleIcon" size={18} className="text-green-500 mt-0.5" variant="solid" />
                                                    <span>{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 flex flex-col justify-center">
                                            <p className="text-slate-700 italic text-sm mb-4">"{activeData.testimonial}"</p>
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden">
                                                    <Icon name="UserIcon" size={20} className="text-slate-400 m-1" variant="solid" />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-900 text-sm">{activeData.author}</div>
                                                    <div className="text-xs text-slate-500">{activeData.role}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SuccessMetrics;